/**
 * Shared constants and helpers for session-expiry / authentication error handling.
 *
 * The apiClient interceptor stores a user-facing message in sessionStorage under
 * SESSION_EXPIRED_STORAGE_KEY before redirecting to /login.  The LoginPage reads
 * that key on mount, displays a warning alert, and then removes the entry.
 *
 * Components that make API calls should guard their catch-blocks with
 * isSessionExpiredError so they do not show a generic error message on the screen
 * immediately before the redirect fires.
 */

/** sessionStorage key used by apiClient and LoginPage. */
export const SESSION_EXPIRED_STORAGE_KEY = 'sessionExpiredMessage';

/** Fixed local message shown when the JWT has expired (error code SESSION_EXPIRED). */
export const SESSION_EXPIRED_MESSAGE = 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.';

/** Fixed local message shown for all other 401 cases (INVALID_TOKEN, UNAUTHORIZED, …). */
export const SESSION_ABGEMELDET_MESSAGE = 'Sie wurden abgemeldet. Bitte melden Sie sich erneut an.';

/**
 * Returns true when the error was caused by an expired or invalid JWT and the
 * interceptor has already initiated a logout + redirect to /login.
 *
 * Usage inside a catch block:
 *   } catch (err) {
 *     if (isSessionExpiredError(err)) return;
 *     setError('...');
 *   }
 *
 * @param {unknown} err - The caught error value.
 * @returns {boolean}
 */
export const isSessionExpiredError = (err) => Boolean(err?.isSessionExpired);

/**
 * Returns true when the request was aborted via an AbortController (e.g. the
 * component unmounted or the user navigated away mid-request). axios surfaces
 * this as a CanceledError with code 'ERR_CANCELED'. Such aborts are expected and
 * must not be treated as real failures.
 *
 * @param {unknown} err - The caught error value.
 * @returns {boolean}
 */
export const isCanceledError = (err) =>
  err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError';

/**
 * Gemeinsames Blob-Error-Parsing für Downloads mit `responseType: 'blob'` (PDF- und
 * Office-Export): Bei einem Fehler liefert der Server einen JSON-Body `{message}`, den Axios
 * wegen des Blob-responseType ebenfalls als Blob in err.response.data ablegt — muss also erst
 * als Text gelesen und geparst werden, bevor die eigentliche Fehlermeldung feststeht.
 *
 * @param {unknown} err - Der gefangene Axios-Fehler.
 * @param {string} fallback - Fallback-Meldung, falls der Body nicht geparst werden kann.
 * @returns {Promise<string>}
 */
export const extractBlobErrorMessage = async (err, fallback) => {
  if (err.response?.data instanceof Blob) {
    try {
      const text = await err.response.data.text();
      const parsed = JSON.parse(text);
      return parsed.message || fallback;
    } catch {
      return fallback;
    }
  }
  return err.response?.data?.message || fallback;
};
